export function validateNode(node) {
  const errors = [];
  if (node.type === 'media' || node.data?.type === 'media') {
    const show = !!(node.show ?? node.data?.show);
    const stimulusId = String((node.stimulus_id ?? node.data?.stimulus_id) || '').trim();
    const src = String((node.media?.src ?? node.data?.media?.src) || '').trim();
    if (!stimulusId) errors.push('Media node: stimulus_id is required.');
    if (show && !src) errors.push('Media node: media.src is required when show=true.');
    if (src && !/^https?:\/\//i.test(src)) errors.push('Media node: media.src must be an absolute HTTPS URL.');
  }
  if (node.data?.type === 'file') {
    const { accept = [], maxSizeMB, storage } = node.data || {};
    if (!Array.isArray(accept) || accept.length === 0) errors.push('File node: "accept" list cannot be empty.');
    const badExt = accept.find(ext => !/^\.[A-Za-z0-9]+$/.test(ext));
    if (badExt) errors.push(`File node: invalid extension "${badExt}"`);
    if (typeof maxSizeMB !== 'number' || maxSizeMB <= 0) errors.push('File node: "maxSizeMB" must be a positive number.');
    if (!['drive','gcs','auto'].includes(storage || 'auto')) errors.push('File node: "storage" must be "drive", "gcs", or "auto".');
  }
  return errors;
}

/**
 * Validate media show/hide flow invariants across the graph.
 * Rules:
 *  - For each media show (show=true, stimulus_id=S), all forward paths must encounter a matching hide (show=false, S) before reaching an END node.
 *  - Disallow paths that exit to END (or another show for S) without a hide.
 * Returns array of issues: { severity:'error', nodeId, message, suggestion }
 */
export function validateMediaPairs(nodes = [], edges = []) {
  const issues = [];
  const byId = Object.fromEntries(nodes.map(n => [n.id, n]));
  const out = new Map();
  edges.forEach(e => {
    if (!out.has(e.source)) out.set(e.source, []);
    out.get(e.source).push(e.target);
  });
  const isEnd = (n) => n?.type === 'end' || n?.data?.type === 'end' || /^end_/i.test(n?.id || '');
  const mediaShows = nodes.filter(n => (n.data?.type === 'media' || n.type === 'media') && (n.data?.show !== false));
  const hidesByStimulus = new Map();
  nodes.forEach(n => {
    if ((n.data?.type === 'media' || n.type === 'media') && n.data?.show === false && n.data?.stimulus_id) {
      const s = String(n.data.stimulus_id).trim();
      if (!hidesByStimulus.has(s)) hidesByStimulus.set(s, new Set());
      hidesByStimulus.get(s).add(n.id);
    }
  });
  function dfs(fromId, stimulusId, visited = new Set()) {
    if (visited.has(fromId)) return { reachesEndWithoutHide: false };
    visited.add(fromId);
    const node = byId[fromId];
    if (!node) return { reachesEndWithoutHide: false };
    // If we hit a matching hide, path is safe
    if ((node.data?.type === 'media' || node.type === 'media') && node.data?.show === false && String(node.data?.stimulus_id||'') === stimulusId) {
      return { reachesEndWithoutHide: false };
    }
    // If we reach END, it's a violation
    if (isEnd(node)) {
      return { reachesEndWithoutHide: true };
    }
    const nextIds = out.get(fromId) || [];
    let anyViolation = false;
    for (const t of nextIds) {
      const r = dfs(t, stimulusId, new Set(visited));
      if (r.reachesEndWithoutHide) anyViolation = true;
    }
    return { reachesEndWithoutHide: anyViolation };
  }
  for (const show of mediaShows) {
    const s = String(show.data?.stimulus_id || '').trim();
    if (!s) continue; // node-level validator will flag
    if (!hidesByStimulus.has(s)) {
      issues.push({ severity: 'error', nodeId: show.id, message: `Media show '${s}' has no corresponding hide in graph.`, suggestion: 'Create a paired hide for this stimulus.' });
      continue;
    }
    const outs = out.get(show.id) || [];
    if (outs.length === 0) {
      issues.push({ severity: 'error', nodeId: show.id, message: `Media show '${s}' does not lead to any node.`, suggestion: 'Connect to the next node and ensure a paired hide exists downstream.' });
      continue;
    }
    const violation = dfs(show.id, s).reachesEndWithoutHide;
    if (violation) {
      issues.push({ severity: 'error', nodeId: show.id, message: `Flow allows reaching END without hiding stimulus '${s}'.`, suggestion: 'Route all paths through a matching hide before END.' });
    }
  }
  return issues;
}
